import {
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { matDropDownStyle } from '../../assets/style/material/matSyles';
import { months } from '../../constants/dates';
import {
  getDateInWeekDayMonthDateFormat,
  getEasternTime
} from '../../helpers/dateHelpers';
import { useProfile } from '../../providers/Profile';
import { useSdoAdminEligibilityCriteriaProvider } from '../../providers/SdoAdminEligibilityCriteriaProvider';
import { useSdoAdminReportsProvider } from '../../providers/SdoAdminReportsProvider';
import AppLoader from '../Loader/AppLoader';
import SdoAdminReportDetailAvailability from './SdoAdminReportDetailAvailability';
import SdoAdminReportDetailsReqList from './SdoAdminReportDetailsReqList';
import './SdoAdminReportDetailsReview.scss';
import {
  getLastPublishedDate,
  getPositionFormatted,
  positionPublishStatus,
  positionReqListColumns,
  sdoRequestlistNoData,
  getNoOfRequests,
  renderIfPositionExist,
  isAllowViewPosition,
  hidePublishedReportData
} from './SdoAdminReportHelper';
import ReplayIcon from '@mui/icons-material/Replay';
import { useSdoApiError } from '../../providers/SdoApiErrorProvider';
import AcDialog from '../AcDialog/AcDialog';
import GetAppIcon from '@mui/icons-material/GetApp';
import { SdoEipData, SdoMonthlyReport } from '../../models/SdoInterface';

export default function SdoAdminReportDetailsReview({
  positionsPublishData,
  setPositionsPublishData,
  position,
  fromSetLimits = false,
  setPosition,
  refresh,
  setRefresh
}) {
  const { t } = useTranslation();
  const sdoAdminReportsContext = useSdoAdminReportsProvider();
  const sdoAdminEligibilityCriteriaContext =
    useSdoAdminEligibilityCriteriaProvider();
  const profileContext = useProfile();
  const [profile, setProfile] = useState<any>();
  const [renderedPosition, setRenderedPosition] = useState('');
  const [selectedPosition, setSelectedPosition] = useState<any>();
  const [status, setStatus] = useState('');
  const dropDownClasses = matDropDownStyle();
  const selectClass = `${dropDownClasses.select} ${dropDownClasses.selectForSdoPositonsFilter}`;
  const [open, setOpen] = useState(false);
  const [closeConfirmation, setCloseConfirmation] = useState(false);
  const [isAllPositions, setIsAllPositions] = useState(false);
  const [selectedPositionIndex, setSelectedPositionIndex] = useState(0);
  const [sdoReqList, setSdoReqList] = useState<any>({});
  const getTotalPositionRequests = () => {
    if (!positionsPublishData) return 0;
    return positionsPublishData?.positions.reduce(
      (acc, position) => acc + Number(position?.totalRequests),
      0
    );
  };

  // Checks and returns #reqCount requests) OR (#reqCount request) OR "" based on SDO request types and count
  const displayRequestCount = () => {
    const totalNumberofCurrentRequests = getTotalPositionRequests();
    const requestCountStringFormatted =
      totalNumberofCurrentRequests > 0
        ? `(${totalNumberofCurrentRequests} ${totalNumberofCurrentRequests > 1 ? 'requests' : 'request'})`
        : '';
    return requestCountStringFormatted;
  };

  const [positionArray, setPositionArray] = useState<any>([
    'Select a Position',
    `All Positions ${displayRequestCount()}`
  ]);
  const [sdoReqListLoading, setSdoReqListLoading] = useState(false);
  const [sdoEipData, setSdoEipData] = useState<SdoEipData | undefined>(undefined);
  const [published, setPublished] = useState(false);
  const [exported, setExported] = useState(false);
  const [flagOtherAdminPublished, setFlagOtherAdminPublished] = useState(false);
  const [manualAction, setManualAction] = useState(false);
  const [availabilityUpdatedByOther, setAvailabilityUpdatedByOther] =
    useState(false);
  const [allPositionPublished, setAllPositionPublished] = useState(false);
  const [autoLoad, setAutoLoad] = useState(false);
  const [limitsData, setLimitsData] = useState<any>({});
  const [sdoReqListObject, setSdoReqListObject] = useState<any>();
  const sdoApiErrorContext = useSdoApiError();
  const [revertOpen, setRevertOpen] = useState(false);
  const handleClose = () => setRevertOpen(false);
  const [report, setReport] = useState<SdoMonthlyReport>();
  const [disableRefresh, setDisableRefresh] = useState(true);

  const [disablePrevNext, setDisablePrevNext] = useState({
    disablePrev: true,
    disableNext: true
  });

  const [sdoMonthYear, setSdoMonthYear] = useState<any>({
    sdoMonth: '',
    sdoYear: '',
    sdoMonthYearString: ''
  });

  const iconComponent = () => {
    return (
      <div onClick={handleOpenClose} className="icon_container">
        <i className="icon-chevron-thin-down" />
      </div>
    );
  };

  const handleOpenClose = () => {
    setOpen(!open);
  };

  const getPreviousPosition = () => {
    if (!selectedPosition) return;
    if (selectedPositionIndex > 2) {
      setSelectedPositionIndex(selectedPositionIndex - 1);
      setAutoLoad(true);
    } else {
      setDisablePrevNext({ ...disablePrevNext, disablePrev: true });
    }
  };

  const getNextPosition = () => {
    if (!selectedPosition) return;
    if (selectedPositionIndex < positionArray.length - 1) {
      setSelectedPositionIndex(selectedPositionIndex + 1);
      setAutoLoad(true);
    } else {
      setDisablePrevNext({ ...disablePrevNext, disableNext: true });
    }
  };

  const allPositions = {
    baseId: 'All',
    equipmentId: 'All',
    positionId: 'All'
  };

  const onPositionChange = (event) => {
    const positionIndex = event.target.value;
    const position =
      positionIndex !== 0
        ? positionIndex !== 1
          ? positionsPublishData?.positions[positionIndex - 2]
          : allPositions
        : {};
    setSelectedPosition(position);
    setRenderedPosition(positionArray[positionIndex]);
    setSelectedPositionIndex(Number(positionIndex));
    setIsAllPositions(positionIndex === 1);

    // on changing the position reset the eip data and the report data
    setSdoReqList({});
    setSdoEipData(undefined);
  };

  // const getTotalRequestsForAllPositions = () => {
  //     if (!positionsPublishData?.positions) return 0;

  //     return positionsPublishData?.positions.reduce((total, position) => {
  //         total = total + position?.totalRequests;
  //         return total;
  //     }, 0);
  // }

  const getOtherAdminsPublishedCount = () => {
    if (!profile) return 0;
    return positionsPublishData?.positions?.filter(
      (position) =>
        position?.status === positionPublishStatus.PUBLISHED &&
        Number(position?.publishedBy) !== Number(profile?.EmployeeId)
    ).length;
  };

  const getSelfPublishedCount = () => {
    if (!profile) return 0;

    const positionsPublishedBySelf = positionsPublishData?.positions?.filter(
      (position) =>
        position?.status === positionPublishStatus.PUBLISHED &&
        Number(position?.publishedBy) === Number(profile?.EmployeeId)
    ).length;
    return positionsPublishedBySelf;
  };

  const resetFlags = () => {
    setCloseConfirmation(false);
    setFlagOtherAdminPublished(false);
    setPublished(false);
    setManualAction(false);
    setAvailabilityUpdatedByOther(false);
    setExported(false);
    sdoApiErrorContext.setSdoErrorCode(0);
  };

  const onClickViewPosition = (resetFlag, autoLoad = false) => {
    resetFlags();
    if (autoLoad) {
      if (selectedPositionIndex === 0) return;
      if (selectedPositionIndex === 1) setIsAllPositions(true);
      else setIsAllPositions(false);
      const sdoReqListObj = {
        base:
          selectedPositionIndex !== 1
            ? selectedPosition?.pos?.base
            : allPositions.baseId,
        equipment:
          selectedPositionIndex !== 1
            ? selectedPosition?.pos?.equipment
            : allPositions.equipmentId,
        position:
          selectedPositionIndex !== 1
            ? selectedPosition?.pos?.position
            : allPositions.positionId,
        month: sdoMonthYear.sdoMonth,
        year: sdoMonthYear.sdoYear,
        reset: resetFlag,
        status: selectedPosition?.status,
        timerType: refresh > 0 ? 'refresh' : 'default'
      };

      // meanwhile, disable the refresh button
      setDisableRefresh(true);

      setSdoReqListObject(sdoReqListObj);
      // to show loader when fetching requests for a position
      setSdoReqListLoading(true);
      // to load the limits api to check if the data is overridden or not (ONLY for single positions)
      if (selectedPositionIndex !== 1) {
        const position = {
          base: selectedPosition?.pos?.base,
          equipment: selectedPosition?.pos?.equipment,
          rank: selectedPosition?.pos?.position
        };
        sdoAdminEligibilityCriteriaContext.viewPosition(position, sdoMonthYear);
      }
      sdoAdminReportsContext.getSdoReqListData(sdoReqListObj);

      // reset publish report data
      sdoAdminReportsContext.setPublishReportData({});

      setAutoLoad(false);
      setRefresh(0);
    }
  };

  function renderValue() {
    const position = renderedPosition.split('(');
    return (
      <span>
        {position[0].replace(/\s/g, '').split('|')[0] === 'undefined'
          ? 'All Positions'
          : position[0]}
        {selectedPositionIndex > 0 && (
          <span style={{ float: 'right', marginRight: '1rem' }}>
            {' '}
            {selectedPositionIndex !== 1
              ? renderIfPositionExist(position)
              : displayRequestCount()}
          </span>
        )}
      </span>
    );
  }

  const onCloseConfirmation = () => {
    setCloseConfirmation(true);
    setExported(false);
  };

  const getSaveLimitsAPIParams = () => {
    const currentMonth = sdoMonthYear.sdoMonth;
    const currentYear = sdoMonthYear.sdoYear;

    setAvailabilityUpdatedByOther(false);
    const position = {
      base: sdoReqListObject?.base,
      equipment: sdoReqListObject?.equipment,
      rank: sdoReqListObject?.position
    };
    const limits = limitsData?.requestList.map((request) => {
      return {
        sdoAvailability: request.sdoAvailability,
        sdoMonth: request.sdoMonth,
        sdoYear: request.sdoYear
      };
    });
    return {
      currentMonth: Number(currentMonth),
      position: position,
      currentYear: Number(currentYear),
      limits: limits
    };
  };

  const onCloseSetManualOverride = () => {
    setSdoReqListLoading(true);
    const params = getSaveLimitsAPIParams();
    sdoAdminEligibilityCriteriaContext.saveAvailabilities(
      params.currentMonth,
      params.position,
      params.currentYear,
      params.limits,
      false
    );
    onClickViewPosition(false, true);
  };

  const getPublishedBy = () => {
    if (selectedPosition && selectedPosition?.publishedBy) {
      return Number(selectedPosition?.publishedBy) ===
        Number(profile?.EmployeeId)
        ? profile?.name
        : 'Other';
    }
  };

  const getPublishedOn = () => {
    if (selectedPosition && selectedPosition?.publishedOn) {
      return getDateInWeekDayMonthDateFormat(
        getEasternTime(selectedPosition?.publishedOn)
      );
    }
  };

  const getPublishedAt = () => {
    if (selectedPosition && selectedPosition?.publishedOn) {
      return getEasternTime(selectedPosition?.publishedOn).toLocaleTimeString();
    }
  };

  const checkIfPositionNotPublished = () => {
    if (selectedPosition?.status && selectedPosition?.status !== 'PUBLISHED') {
      return true;
    } else {
      return false;
    }
  };

  const getSelectedPosition = () => {
    const position = positionsPublishData?.positions?.filter(
      (position) =>
        position?.pos?.base === selectedPosition?.pos?.base &&
        position?.pos?.equipment === selectedPosition?.pos?.equipment &&
        position?.pos?.position === selectedPosition?.pos?.position
    )[0];

    setPosition({
      ...position
    });
  };

  useEffect(() => {
    onClickViewPosition(false, autoLoad);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPosition]);

  useEffect(() => {
    if (!fromSetLimits || !position || !positionsPublishData?.positions) return;
    onClickViewPosition(false, true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromSetLimits, positionsPublishData?.positions]);

  useEffect(() => {
    if (!profileContext?.profile?.results) return;
    setProfile(profileContext?.profile?.results);
  }, [profileContext.profile]);

  useEffect(() => {
    if (!sdoAdminEligibilityCriteriaContext.positionAvailabilityData) return;

    setLimitsData(sdoAdminEligibilityCriteriaContext.positionAvailabilityData);
  }, [sdoAdminEligibilityCriteriaContext.positionAvailabilityData]);

  useEffect(() => {
    if (
      !positionsPublishData?.sdoMonth &&
      !positionsPublishData?.sdoYear &&
      !positionsPublishData?.positions
    )
      return;

    sdoApiErrorContext.setSdoErrorCode(0);
    setAvailabilityUpdatedByOther(false);
    setSdoMonthYear({
      sdoMonth: positionsPublishData?.sdoMonth,
      sdoYear: positionsPublishData?.sdoYear,
      sdoMonthYearString:
        months[Number(positionsPublishData?.sdoMonth) - 1] +
        ' ' +
        positionsPublishData?.sdoYear
    });
    setStatus(positionsPublishData?.cycleStatus);

    if (
      Number(positionsPublishData?.totalPublished) +
        Number(positionsPublishData?.totalPending) ===
      Number(positionsPublishData?.totalPublished)
    ) {
      setAllPositionPublished(true);
    }
    const arr = positionsPublishData?.positions.map(
      (position) =>
        `${getPositionFormatted(position)}${getNoOfRequests(position)}`
    );
    setPositionArray((prev) => [...prev, ...arr]);
    setRenderedPosition(positionArray[0]);

    // reset publish report data
    sdoAdminReportsContext.setPublishReportData({});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positionsPublishData]);

  useEffect(() => {
    if (
      !position ||
      (getTotalPositionRequests() === 0 &&
        !isAllowViewPosition(positionsPublishData?.positions))
    ) {
      setAutoLoad(false);
      return;
    }

    const positionUpdated = positionsPublishData.positions.find(
      (positionIndex) =>
        positionIndex.pos.base === position.pos?.base &&
        positionIndex.pos.equipment === position.pos?.equipment &&
        positionIndex.pos.position === position.pos?.position
    );
    const indexOfSelectedPosition: any =
      positionsPublishData?.positions.indexOf(positionUpdated);

    setSelectedPosition(position);
    setAutoLoad(true);
    setSelectedPositionIndex(indexOfSelectedPosition + 2);
    setRenderedPosition(
      `${getPositionFormatted(position)}${getNoOfRequests(position)}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position]);

  useEffect(() => {
    if (selectedPositionIndex > 1) {
      const position =
        positionsPublishData?.positions[selectedPositionIndex - 2];
      setSelectedPosition(
        positionsPublishData?.positions[selectedPositionIndex - 2]
      );
      setRenderedPosition(
        `${getPositionFormatted(position)}${getNoOfRequests(position)}`
      );
    }

    if (selectedPositionIndex > 2) {
      setDisablePrevNext({ disableNext: false, disablePrev: false });
    }

    if (selectedPositionIndex === 2) {
      setDisablePrevNext({ disableNext: false, disablePrev: true });
    }

    if (selectedPositionIndex === positionArray.length - 1) {
      setDisablePrevNext({ disablePrev: false, disableNext: true });
    }

    if (selectedPositionIndex === 0 || selectedPositionIndex === 1) {
      setDisablePrevNext({ disablePrev: true, disableNext: true });
      // selectedPositionIndex === 1 ? setIsAllPositions(true) : setIsAllPositions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPositionIndex]);

  useEffect(() => {
    if (!sdoAdminReportsContext.sdoReqListData?.reports) {
      setSdoReqList({});
      setSdoReqListLoading(false);
      return;
    }

    if (sdoReqListLoading) {
      setSdoReqList(sdoAdminReportsContext.sdoReqListData);
      // set the EIP data info
      setSdoEipData(sdoAdminReportsContext.sdoReqListData?.eipData);

      // to set the manual edit flag
      const reports = sdoAdminReportsContext.sdoReqListData?.reports.filter(
        (report) => Number(report?.updatedBy) !== -1 && !report?.isPreApproved
      );
      if (reports && reports.length > 0) setManualAction(true);
      else setManualAction(false);
      setSdoReqListLoading(false);
      // enable the refresh button since requests are fetched now!
      setDisableRefresh(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdoAdminReportsContext.sdoReqListData]);

  useEffect(() => {
    if (!sdoAdminReportsContext.publishReportData?.message) return;

    if (sdoAdminReportsContext.publishReportData?.message !== 'success') {
      setFlagOtherAdminPublished(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdoAdminReportsContext.publishReportData]);

  useEffect(() => {
    if (!sdoAdminEligibilityCriteriaContext.positionAvailabilityData) return;

    if (
      sdoAdminEligibilityCriteriaContext.positionAvailabilityData
        ?.manualOverride
    ) {
      setAvailabilityUpdatedByOther(true);
    } else {
      setAvailabilityUpdatedByOther(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdoAdminEligibilityCriteriaContext.positionAvailabilityData]);

  useEffect(() => {
    setPublished(false);
  }, [selectedPosition]);

  useEffect(() => {
    if (!sdoAdminReportsContext?.sdoMonthlyReportsData?.sdoSummary) return;
    setReport(sdoAdminReportsContext?.sdoMonthlyReportsData);
  }, [sdoAdminReportsContext.sdoMonthlyReportsData]);

  useEffect(() => {
    if (!report) return;
    if (!sdoAdminReportsContext?.sdoMonthlyReportsLoading) {
      // downloadSdoMonthlyReport();
      sdoAdminReportsContext.setSdoMonthlyReportsLoading(true);
      setCloseConfirmation(false);
      setExported(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  // const downloadSdoMonthlyReport = async () => {
  //   await download('xlsx', getWorkBook(report, profileContext), report);
  // };

  const handleOpenRevertPopUp = () => {
    setRevertOpen(true);
  };

  const handleRevertBack = async () => {
    onClickViewPosition(true, true);
    handleClose();
  };

  /*
   * Once All positions are published ::
   * below function will download the netline file
   * and download the excel export data for that month
   */
  const handleExportFile = async () => {
    //  api call and generate a text file
    await sdoAdminReportsContext?.getNetlineDataAndExport({
      sdoMonth: sdoMonthYear?.sdoMonth,
      sdoYear: sdoMonthYear?.sdoYear
    });
    // export the excel sheet
    await sdoAdminReportsContext.getSdoMonthlyReportsData({
      month: sdoMonthYear.sdoMonth,
      year: sdoMonthYear.sdoYear
    });
  };

  const revertData = {
    title: t(
      'specialDayOffAdmin.specialDayOffAdminReportDetails.revertRequestTitle'
    ),
    description: t(
      'specialDayOffAdmin.specialDayOffAdminReportDetails.revertRequestText'
    ),
    actionLabel: t(
      'specialDayOffAdmin.specialDayOffAdminReportDetails.revertRequestCancel'
    ),
    handleAction: handleClose,
    secondaryActionLabel: t(
      'specialDayOffAdmin.specialDayOffAdminReportDetails.revertRequestConfirm'
    ),
    handleSecondaryAction: handleRevertBack
  };

  const getEipDate = () => {
    if (sdoEipData
      && sdoEipData.eipStartDate
      && sdoEipData.eipEndDate) {
      const eipStartDate = sdoEipData.eipStartDate.split(' ');
      const eipEndDate = sdoEipData.eipEndDate.split(' ');

      if(eipStartDate.length < 4 || eipEndDate.length < 4) return '';

      if (!isAllPositions) return `${eipStartDate[0]} ${eipStartDate[1]}, ${eipStartDate[2]} ${eipStartDate[3]} ET`;

      if (eipStartDate[0] === eipEndDate[0]
        && (eipStartDate[1] === eipEndDate[1])
        && (eipStartDate[2] === eipEndDate[2])
      ) {
        return `${eipStartDate[0]} ${eipStartDate[1]}, ${eipStartDate[2]} ${eipStartDate[3]} ET - ${eipEndDate[3]} ET`
      }
      else {
        return `${eipStartDate[0]} ${eipStartDate[1]}, ${eipStartDate[2]} ${eipStartDate[3]} ET - ${eipEndDate[0]} ${eipEndDate[1]}, ${eipEndDate[2]} ${eipEndDate[3]} ET`;
      }
    }
    return '';
  }

  const getEipTime = () => {
    if (sdoEipData && sdoEipData.duration) {
      const eipDuration = sdoEipData.duration.split(':');
      if(eipDuration 
        && eipDuration.length > 0 
        && eipDuration.length === 3 
        && eipDuration[0] 
        && eipDuration[1] 
        && eipDuration[2]) {
          const seconds = Number(eipDuration[2]) < 10 ? eipDuration[2].split('')[1] : eipDuration[2];
          const minutes = Number(eipDuration[1]) < 10 ? eipDuration[1].split('')[1] : eipDuration[1];
          const hours = Number(eipDuration[0]) < 10 ? eipDuration[0].split('')[1] : eipDuration[0];
        
        if(eipDuration[0] === '00' && eipDuration[1] === '00') {
          return `${seconds} secs`;
        }
        else if(eipDuration[0] === '00') {
          return `${minutes} mins ${seconds} secs`;
        }
        else {
          return `${hours} hrs ${minutes} mins ${seconds} secs`;
        }
      }      
    }
    return '';
  }

  const handleOnClickRefresh = () => {
    // Sync the position in the parent component to the selected position 
    // for the refresh to work properly
    getSelectedPosition();
    setRefresh((prevState) => (prevState += 1));
  }

  return (
    <div className="sdo-report-review__container">
      <div className="sdo-report-review__container-inside">
        <div
          id="sdo-report-review__reload"
          className={
            disableRefresh
              ? 'sdo-report-review__reload sdo-report-review__reload--disabled'
              : 'sdo-report-review__reload'
          }
        >
          <div onClick={handleOnClickRefresh}>
            <span>
              <span className="reset-button-link no-padding">Refresh</span>
              <ReplayIcon
                viewBox="-4,0,24,24"
                fontSize="large"
                className="replay-icon"
              />
            </span>
          </div>          
        </div>
        {allPositionPublished ? (
          <div className="sdo-report-review__export-files">
            <div className="sdo-report-review__export-files--info">
              <span>SDO {sdoMonthYear.sdoMonthYearString} </span>
              <span>
                {t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.exportFilesDescription'
                )}
              </span>
            </div>
            <div
              className="sdo-report-review__export-files--icon"
              onClick={() => handleExportFile()}
            >
              <GetAppIcon style={{ fontSize: 20, color: '#005078' }} />
              <span>
                {t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.exportFiles'
                )}
              </span>
            </div>
          </div>
        ) : (
          ''
        )}
        <div className="sdo-report-review__header">
          <p className="sdo-report-review__header--label">
            {t(
              'specialDayOffAdmin.specialDayOffAdminReportDetails.reviewRequests'
            )}{' '}
          </p>
          {!isAllPositions ? (
            <div className="sdo-report-review__header--buttons">
              <button
                className="sdo-report-review__header-button"
                disabled={disablePrevNext.disablePrev}
                onClick={() => getPreviousPosition()}
              >
                {' '}
                <span className="icon-chevron-thin-left"></span>{' '}
                {t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.previous'
                )}{' '}
              </button>
              <button
                className="sdo-report-review__header-button sdo-report-review__header-button--next"
                disabled={disablePrevNext.disableNext}
                onClick={() => getNextPosition()}
              >
                {' '}
                {t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.next'
                )}{' '}
                <span className="icon-chevron-thin-right"></span>
              </button>
            </div>
          ) : (
            ''
          )}
        </div>
        <p className="sdo-report-review__info-text">
          {t('specialDayOffAdmin.specialDayOffAdminReportDetails.selectReport')}
        </p>
        <div className="sdo-report-review__position-header">
          <div className="sdo-report-review__position__select">
            <FormControl variant="standard" className="select-dropdown">
              <InputLabel id="inputLabel" className={dropDownClasses.label}>
                {t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.position'
                )}
              </InputLabel>
              <Select
                disableUnderline
                className={selectClass}
                onChange={onPositionChange}
                onClose={handleOpenClose}
                onOpen={handleOpenClose}
                open={open}
                name={'Position'}
                label={t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.position'
                )}
                renderValue={renderValue}
                value={positionArray[0]}
                IconComponent={iconComponent}
                MenuProps={{ className: 'menu-root' }}
              >
                {positionArray.map((positionItem, index) => {
                  return (
                    <MenuItem
                      style={{ fontSize: '1.6rem' }}
                      value={index}
                      key={index}
                      className={dropDownClasses.menuListItem}
                    >
                      {positionItem}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </div>
          <Button
            disabled={selectedPositionIndex === 0 ? true : false}
            className={
              selectedPositionIndex === 0
                ? 'sdo-report-review__position__search-button--disabled'
                : 'sdo-report-review__position__search-button'
            }
            onClick={() => {
              // onClickViewPosition(false, true);
              setAutoLoad(true);
              getSelectedPosition();
            }}
          >
            {t(
              'specialDayOffAdmin.specialDayOffAdminReportDetails.viewPosition'
            )}
          </Button>          
        </div>
        { sdoReqListLoading ? '' : 
          <div className="sdo-report-review__position-header">
            <div className="sdo-report-review__eip-data">
              {
                sdoEipData ? (
                  <p> {t(
                    'specialDayOffAdmin.specialDayOffAdminReportDetails.eipData',
                    { eipDate: getEipDate(), duration: isAllPositions ? 'Total duration' : 'Duration',eipTime: getEipTime() }
                  )}</p>
                ) : ''
              }
            </div>
            {selectedPositionIndex > 0 && (
              <div className="sdo-report-review__position__publish-status">
                {/* //* publish - pending && published */}
                {isAllPositions ? (
                  !allPositionPublished ? (
                    // * publish - pending (All)
                    <div className="sdo-report-review__position__publish--pending">
                      <div className="sdo-report-review__position__all-publish--pending">
                        <div className="sdo-report-review__position__all-publish-status">
                          <i className="icon-clock sdo-report-status__positions-icon"></i>
                          <p>
                            {positionsPublishData?.totalPending}{' '}
                            {t(
                              'specialDayOffAdmin.specialDayOffAdminReportDetails.pendingPublications'
                            )}
                          </p>
                        </div>

                        <p className="sdo-report-review__position__published-by--all">
                          {getSelfPublishedCount() ? (
                            <>
                              <span>
                                {t(
                                  'specialDayOffAdmin.specialDayOffAdminReportDetails.otherPublished',
                                  { otherPublished: getSelfPublishedCount() }
                                )}
                              </span>
                              {t(
                                'specialDayOffAdmin.specialDayOffAdminReportDetails.publishedBy',
                                {
                                  publishedBy: profile?.name,
                                  publishedOn: getDateInWeekDayMonthDateFormat(
                                    new Date(
                                      getLastPublishedDate(
                                        profile,
                                        positionsPublishData?.positions
                                      )
                                    )
                                  ),
                                  publishedAt: new Date(
                                    getLastPublishedDate(
                                      profile,
                                      positionsPublishData?.positions
                                    )
                                  ).toLocaleTimeString()
                                }
                              )}
                            </>
                          ) : (
                            ''
                          )}
                          {getOtherAdminsPublishedCount() ? (
                            <span>
                              {t(
                                'specialDayOffAdmin.specialDayOffAdminReportDetails.otherPublished',
                                { otherPublished: getOtherAdminsPublishedCount() }
                              )}{' '}
                              <span>
                                {t(
                                  'specialDayOffAdmin.specialDayOffAdminReportDetails.otherPublishedBy'
                                )}
                              </span>
                            </span>
                          ) : (
                            ''
                          )}
                        </p>
                      </div>
                    </div>
                  ) : (
                    // * publish - published (All)
                    !hidePublishedReportData(positionsPublishData) && (
                      <div className="sdo-report-review__position__publish--done">
                        <div className="sdo-report-review__position__published">
                          <i className="icon-Confirmation-circle sdo-report-status__positions-icon"></i>
                          <p>
                            {t(
                              'specialDayOffAdmin.specialDayOffAdminReportDetails.reportAllPublished'
                            )}
                          </p>
                        </div>

                        <p className="sdo-report-review__position__published-by--all">
                          {getSelfPublishedCount() ? (
                            <>
                              <span>
                                {t(
                                  'specialDayOffAdmin.specialDayOffAdminReportDetails.otherPublished',
                                  { otherPublished: getSelfPublishedCount() }
                                )}
                              </span>
                              {t(
                                'specialDayOffAdmin.specialDayOffAdminReportDetails.publishedBy',
                                {
                                  publishedBy: profile?.name,
                                  publishedOn: getDateInWeekDayMonthDateFormat(
                                    new Date(
                                      getLastPublishedDate(
                                        profile,
                                        positionsPublishData?.positions
                                      )
                                    )
                                  ),
                                  publishedAt: new Date(
                                    getLastPublishedDate(
                                      profile,
                                      positionsPublishData?.positions
                                    )
                                  ).toLocaleTimeString()
                                }
                              )}
                            </>
                          ) : (
                            ''
                          )}

                          {getOtherAdminsPublishedCount() ? (
                            <span>
                              {t(
                                'specialDayOffAdmin.specialDayOffAdminReportDetails.otherPublished',
                                { otherPublished: getOtherAdminsPublishedCount() }
                              )}
                              <span>
                                {t(
                                  'specialDayOffAdmin.specialDayOffAdminReportDetails.otherPublishedBy'
                                )}
                              </span>
                            </span>
                          ) : (
                            ''
                          )}
                        </p>
                      </div>
                    )
                  )
                ) : checkIfPositionNotPublished() ? (
                  //* publish - pending (single)
                  <div className="sdo-report-review__position__publish--pending">
                    <p>
                      <i className="icon-clock sdo-report-status__positions-icon"></i>
                      {t(
                        'specialDayOffAdmin.specialDayOffAdminReportDetails.pendingPublication'
                      )}
                    </p>
                  </div>
                ) : selectedPosition?.baseId !== 'All' &&
                  selectedPosition?.totalRequests > 0 &&
                  selectedPosition?.publishedOn !== '' &&
                  selectedPosition?.publishedBy !== '' ? (
                  //* publish - published (single)
                  <div className="sdo-report-review__position__publish--done">
                    <div className="sdo-report-review__position__published">
                      <i className="icon-Confirmation-circle sdo-report-status__positions-icon"></i>
                      <p>
                        {t(
                          'specialDayOffAdmin.specialDayOffAdminReportDetails.reportPublished'
                        )}
                      </p>
                    </div>
                    <p className="sdo-report-review__position__published-by">
                      {t(
                        'specialDayOffAdmin.specialDayOffAdminReportDetails.publishedBy',
                        {
                          publishedBy: getPublishedBy(),
                          publishedOn: getPublishedOn(),
                          publishedAt: getPublishedAt()
                        }
                      )}
                    </p>
                  </div>
                ) : (
                  ''
                )}
              </div>
            )}
          </div>
        }
        {/* banner -- availability changed by other Admin */}
        {availabilityUpdatedByOther &&
        sdoReqList &&
        sdoReqList.reports &&
        sdoReqList.reports.length &&
        !sdoReqListLoading &&
        !(
          (sdoReqListObject?.status &&
            sdoReqListObject?.status === 'PUBLISHED') ||
          allPositionPublished
        ) ? (
          <div
            className={
              closeConfirmation
                ? 'sdo-report-review__published-close'
                : 'sdo-report-review__published-other'
            }
          >
            <div className="sdo-report-review__published-info">
              <i className="icon-circle-flight sdo-report-review__published-icon--check"></i>
              <p>
                {t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.availabilityChangedByOther'
                )}
              </p>
            </div>
            <span
              onClick={onCloseSetManualOverride}
              className="sdo-report-review__acknowledge"
            >
              I understand
            </span>
            {/* <i className="icon-close sdo-report-review__published-icon--close" onClick={onCloseConfirmation}></i> */}
          </div>
        ) : (
          ''
        )}
        {/* banner -- report published by other Admin */}
        {!isAllPositions && flagOtherAdminPublished && !sdoReqListLoading ? (
          <div
            className={
              closeConfirmation
                ? 'sdo-report-review__published-close'
                : 'sdo-report-review__published-other'
            }
          >
            <div className="sdo-report-review__published-info">
              <i className="icon-circle-flight sdo-report-review__published-icon--check"></i>
              <p>
                {t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.alreadyPublishedByOther'
                )}
              </p>
            </div>
            <i
              className="icon-close sdo-report-review__published-icon--close"
              onClick={onCloseConfirmation}
            ></i>
          </div>
        ) : (
          ''
        )}
        {/* banner -- report published single/all */}
        {!flagOtherAdminPublished && published && !sdoReqListLoading ? (
          <div
            className={
              closeConfirmation
                ? 'sdo-report-review__published-close'
                : 'sdo-report-review__published'
            }
          >
            <div className="sdo-report-review__published-info">
              <i className="icon-Confirmation-circle sdo-report-review__published-icon--check"></i>
              {!isAllPositions ? (
                <p>
                  {t(
                    'specialDayOffAdmin.specialDayOffAdminReportDetails.reportPublishedSuccess'
                  )}
                </p>
              ) : (
                <p>
                  {t(
                    'specialDayOffAdmin.specialDayOffAdminReportDetails.allReportPublishedSuccess',
                    { monthYear: sdoMonthYear.sdoMonthYearString }
                  )}
                  <span>
                    {t(
                      'specialDayOffAdmin.specialDayOffAdminReportDetails.allReportPublishedSuccessInfo'
                    )}
                  </span>
                </p>
              )}
            </div>
            <i
              className="icon-close sdo-report-review__published-icon--close"
              onClick={onCloseConfirmation}
            ></i>
          </div>
        ) : (
          ''
        )}

        {/* banner -- report exported */}
        {exported ? (
          <div
            className={
              closeConfirmation
                ? 'sdo-report-review__exported-close'
                : 'sdo-report-review__exported'
            }
          >
            <div className="sdo-report-review__exported-info">
              <p>
                {t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.reportExported',
                  { monthYear: sdoMonthYear.sdoMonthYearString }
                )}
              </p>
            </div>
            <i
              className="icon-close sdo-report-review__exported-icon--close"
              onClick={onCloseConfirmation}
            ></i>
          </div>
        ) : (
          ''
        )}
        <AcDialog open={revertOpen} onClose={handleClose} data={revertData} />
        {sdoReqListLoading ? (
          <AppLoader
            loaderText={t(
              'specialDayOffAdmin.specialDayOffAdminReportDetails.loaderText'
            )}
            descriptionText={
              sdoAdminReportsContext?.pendingPositions
                ? t(
                    'specialDayOffAdmin.specialDayOffAdminReportDetails.descriptionText',
                    {
                      pendingPositions: sdoAdminReportsContext?.pendingPositions
                    }
                  )
                : ''
            }
          />
        ) : sdoReqList && sdoReqList.reports && sdoReqList.reports.length ? (
          <>
            <SdoAdminReportDetailAvailability
              sdoReqList={sdoReqList}
              isAllPositions={isAllPositions}
              setPublished={setPublished}
              setExported={setExported}
              setSdoReqListLoading={setSdoReqListLoading}
              sdoReqListObject={sdoReqListObject}
              setSdoReqListObject={setSdoReqListObject}
              manualAction={manualAction}
              status={status}
              allPositionPublished={allPositionPublished}
              availabilityUpdatedByOther={availabilityUpdatedByOther}
              positionsPublishData={positionsPublishData}
              setPositionsPublishData={setPositionsPublishData}
              position={position}
              setPosition={setPosition}
              setAllPositionPublished={setAllPositionPublished}
            />
            <div className="sdo-report-review__req-info">
              <p className="sdo-report-review__req-info-header">
                {t(
                  'specialDayOffAdmin.specialDayOffAdminReportDetails.requestsInformation'
                )}
              </p>
              <div className="sdo-report-review__req-info-container">
                <p className="sdo-report-review__req-info-total">
                  {t(
                    'specialDayOffAdmin.specialDayOffAdminReportDetails.results'
                  )}{' '}
                  {sdoReqList?.reports?.length}
                </p>
                {manualAction &&
                (isAllPositions
                  ? !allPositionPublished
                  : checkIfPositionNotPublished()) ? (
                  <div className="sdo-report-review__req-info-revert">
                    <div
                      className="sdo-report-header__reload"
                      onClick={() => handleOpenRevertPopUp()}
                    >
                      <span className="reset-button-link no-padding">
                        {t(
                          'specialDayOffAdmin.specialDayOffAdminReportDetails.revert'
                        )}
                      </span>
                      <ReplayIcon
                        viewBox="-4,0,24,24"
                        fontSize="large"
                        className="replay-con"
                      />
                    </div>
                  </div>
                ) : (
                  ''
                )}
              </div>
              <SdoAdminReportDetailsReqList
                sdoReqList={sdoReqList}
                setSdoReqList={setSdoReqList}
                isAllPositions={isAllPositions}
                setSdoReqListLoading={setSdoReqListLoading}
                sdoReqListObject={sdoReqListObject}
                setManualAction={setManualAction}
                manualAction={manualAction}
                status={status}
                availabilityUpdatedByOther={availabilityUpdatedByOther}
                renderedPosition={renderedPosition}
              />
            </div>
          </>
        ) : (
          <div className="sdo-report-review__grid-no-data">
            <p className="sdo-report-review__req-info-header">
              {t(
                'specialDayOffAdmin.specialDayOffAdminReportDetails.requestsInformation'
              )}
            </p>
            <DataGridPro
              autoHeight
              disableColumnMenu
              //disableSelectionOnClick
              //disableExtendRowFullWidth={true}
              rows={sdoRequestlistNoData}
              columns={positionReqListColumns}
            />
          </div>
        )}
      </div>
    </div>
  );
}
